import React, { useEffect, Suspense } from 'react';
import './global.css';
import './axios-global-autoclean'; //  One-time setup
import './services/sessionEviction'; //  One-time setup: "signed in on another device" -> login page with the reason
import './services/tenantHostAccess'; //  One-time setup: refused on an institute's domain -> login page with the reason
// SEO Integration
import { RouteBasedSEO } from '@/components/SEO';
import { PageLoadingFallback } from '@/routes/LazyRoutes';
import TenantRouteGuard from '@/components/shared/TenantRouteGuard';
import { isPlatformHost } from '@/http';
import { Toaster } from '@/components/ui/toaster';
import { createRoot } from 'react-dom/client';
import { Toaster as Sonner } from './components/ui/sonner';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, ProtectedRoute } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { BrandingProvider } from './contexts/BrandingContext';
import { GlobalUploadProvider } from './contexts/GlobalUploadContext';
import GlobalUploadManager from './components/UploadManager/GlobalUploadManager';
import { GlobalDownloadProvider } from './contexts/GlobalDownloadContext';
import GlobalDownloadManager from './components/DownloadManager/GlobalDownloadManager';
import { HelmetProvider } from 'react-helmet-async';
import Index from './pages/public/Index';
import Login from './pages/public/Login';
import SimpleSignUp from './pages/public/SignUp';
import ForgotPassword from './pages/public/ForgotPassword';
import Terms from './pages/public/Terms';
import Privacy from './pages/public/Privacy';
import SignOut from './pages/public/SignOut';
import NotFound from './pages/public/NotFound';
const SuperAdminDashboardNew = React.lazy(() => import('./pages/superadmin-new/SuperAdminLandingPage'));
// The FMEA product modeller is a full-screen canvas, so it gets its own
// route rather than a tab: a diagram you cannot link to is a diagram you
// cannot send to the colleague who has to review it.
const ProductModellerPage = React.lazy(() => import('./pages/fmea/ProductModellerPage'));
const Join = React.lazy(() => import('./pages/public/Join'));
const OurTeam = React.lazy(() => import('./pages/public/OurTeam'));
// Hands the signed-in refresh token to a local build script. Lazy: it is
// opened a few times a month by one person and has no business in the bundle
// everyone else downloads.
const CliAuth = React.lazy(() => import('./pages/CliAuth/CliAuth'));
const CreateSessionFinal = React.lazy(() => import('./pages/shared/CreateSessionFinal'));
const LiveSessionControl = React.lazy(() => import('./pages/shared/LiveSessionControl'));
// OLD IMPLEMENTATION - moved to .bak
// const StudentSession = React.lazy(() => import('./pages/student/StudentSession'));
// const Analytics = React.lazy(() => import('./pages/admin/Analytics'));
const MySessions = React.lazy(() => import('./pages/shared/MySessions'));
const Notifications = React.lazy(() => import('./pages/shared/Notifications'));
const HelpCenter = React.lazy(() => import('./pages/shared/HelpCenter/HelpCenter'));
const HelpCenterRoute = React.lazy(() => import('./pages/shared/HelpCenter/HelpCenterRoute'));
// const Reports = React.lazy(() => import('./pages/shared/Reports'));
const Settings = React.lazy(() => import('./pages/shared/Settings'));
const Profile = React.lazy(() => import('./pages/shared/Profile'));
const SessionManagement = React.lazy(() => import('./pages/shared/SessionManagement/SessionManagement'));
const CourseManagement = React.lazy(() => import('./pages/shared/CourseManagement/CourseManagement'));
const CurrentAffairsPage = React.lazy(() => import('./pages/current_affairs').then(m => ({ default: m.CurrentAffairsPage })));
const CurrentAffairsUserPage = React.lazy(() => import('./pages/current_affairs_user/CurrentAffairsPage'));
// Competitive Exams User Portal
const ExamsLandingPage = React.lazy(() => import('./pages/exams_user').then(m => ({ default: m.ExamsLandingPage })));
const CompetitiveExamDetailPage = React.lazy(() => import('./pages/exams_user').then(m => ({ default: m.ExamDetailPage })));
// Online Tests User Portal
const TestsLandingPage = React.lazy(() => import('./pages/test_user').then(m => ({ default: m.TestsLandingPage })));
// OLD IMPLEMENTATION - moved to .bak
// const UserManagement = React.lazy(() => import('./pages/admin/UserManagement/UserManagement'));
const CourseContentView = React.lazy(() => import('./pages/shared/CourseContentView'));
const InteractiveContentGenerator = React.lazy(() => import('./pages/shared/InteractiveContentGenerator'));
// OLD IMPLEMENTATION - moved to .bak
// const StudentPreviewPage = React.lazy(() => import('./pages/student/StudentPreviewPage/StudentPreviewPage'));
// const ComprehensiveDemoTest = React.lazy(() => import('./pages/shared/ComprehensiveDemoTest'));
const InteractiveInstructor = React.lazy(() => import('./pages/interactive').then(m => ({ default: m.InteractiveInstructor })));
const InteractiveSession = React.lazy(() => import('./pages/interactive').then(m => ({ default: m.InteractiveSession })));
const InteractiveSessionList = React.lazy(() => import('./pages/interactive').then(m => ({ default: m.InteractiveSessionList })));
const StudentInteractiveSession = React.lazy(() => import('./pages/interactive').then(m => ({ default: m.StudentInteractiveSession })));
const WaitingPage = React.lazy(() => import('./pages/interactive').then(m => ({ default: m.WaitingPage })));
const InvalidPage = React.lazy(() => import('./pages/interactive/InvalidPage'));
const LeaderboardDashboard = React.lazy(() => import('./pages/interactive/leaderboard/LeaderboardDashboard'));
const QGenerator = React.lazy(() => import('./pages/QGenerator/QGenerator'));
const AITeacherPage = React.lazy(() => import('./pages/AITeacher/AITeacherPage'));
const AITeacherLauncher = React.lazy(() => import('./pages/AITeacher/AITeacherLauncher'));
const AiLimitHost = React.lazy(() => import('./pages/AITeacher/usage/aiLimitBus'));
const AISyncPage = React.lazy(() => import('./pages/AITeacher/AISyncPage'));
const AIInsightsPage = React.lazy(() => import('./pages/AITeacher/AIInsightsPage'));
const QGeneratorFullpage = React.lazy(() => import('./pages/QGenerator/QGeneratorFullpage'));
const UserManual = React.lazy(() => import('./pages/shared/UserManual/UserManual'));
const SimplifiedCoursePage = React.lazy(() => import('./pages/course_page/CoursePageSimplifiedRefactored'));
const MarketHomePage = React.lazy(() => import('./features/marketplace/pages/MarketHomePage'));
const MarketBrowsePage = React.lazy(() => import('./features/marketplace/pages/MarketBrowsePage'));
const MarketItemPage = React.lazy(() => import('./features/marketplace/pages/MarketItemPage'));
const TestPortal = React.lazy(() => import('./pages/TestPortal'));
const CategoryPage = React.lazy(() => import('./pages/TestPortal').then(m => ({ default: m.CategoryPage })));
const PaymentCourseModal = React.lazy(() => import('./pages/PaymentManagement/PaymentCourseModal'));

// Certificate Management Components
const CertificateAnalytics = React.lazy(() => import('./pages/shared/CertificateManagement/CertificateAnalytics'));
const TestCertificate = React.lazy(() => import('./pages/shared/CertificateManagement/TestCertificate'));
const EnhancedCertificateTemplates = React.lazy(() => import('./pages/shared/CertificateManagement/EnhancedCertificateTemplates'));
const BulkCertificateIssuance = React.lazy(() => import('./pages/shared/CertificateManagement/BulkCertificateIssuance'));
const ImprovedCertificateBuilder = React.lazy(() => import('./pages/shared/CertificateManagement/ImprovedCertificateBuilder'));
const VideoPlayerPage = React.lazy(() => import('./pages/course_page/components/MainContent/components/VideoSection/VideoPlayerPage'));
const VideoWatchLinkPage = React.lazy(() => import('./pages/course_page/components/MainContent/components/VideoSection/VideoWatchLinkPage'));
const Evaluate = React.lazy(() => import('./pages/course_page/components/MainContent/components/SelfEvaluation/Evaluate'));
const EvaluateByAI = React.lazy(() => import('./pages/course_page/components/MainContent/components/SelfEvaluation/evaluate/EvaluateByAI'));
const AssessmentMainAI = React.lazy(() => import('./pages/assessment').then(m => ({ default: m.AssessmentMainAI })));
// Editor components

const EditorDemo = React.lazy(() => import('./pages/editor/EditorDemo'));
const RichEditorDemo = React.lazy(() => import('./pages/shared/RichEditorDemo'));


// Test Interface Components
const TestPortalIndex = React.lazy(() => import('./test_interface/pages/Index/Index'));
const TestSelection = React.lazy(() => import('./test_interface/pages/TestSelection/TestSelection'));
const TestInstructions = React.lazy(() => import('./test_interface/pages/TestInstructions/TestInstructions'));
const TestInterface = React.lazy(() => import('./test_interface/pages/TestInterface/TestInterface'));
const TestDemo = React.lazy(() => import('./test_interface/pages/TestDemo/TestDemo'));
const TestResults = React.lazy(() => import('./test_interface/pages/TestResults/TestResults'));
// A published answer sheet's result, opened in its own tab from the results list.
const WrittenResult = React.lazy(() => import('./test_interface/pages/WrittenResult/WrittenResult'));
const QuestionPaperGenerator = React.lazy(() => import('./test_interface/pages/QuestionPaperGenerator/QuestionPaperGenerator'));

import AlertModal from './pages/CommonAlert/AlertModal';
// Test Interface Components
// import TokenIssueNotification from './components/TokenIssueNotification/TokenIssueNotification';
// import { autoSetupDevelopmentAuth } from './utils/developmentAuth';

// Batch Management Components
const ReferralLandingPage = React.lazy(() => import('./pages/referral_landingPage/ReferralLandingPage'));
// Public page an asset's QR label opens (Assets & Finance). No sign-in.
const AssetScanPage = React.lazy(() => import('./pages/superadmin-new/components/AssetsFinance/Public/AssetScanPage'));
// import { autoSetupDevelopmentAuth } from './utils/developmentAuth';
// import AdvancedImageCropperDemo from './pages/superadmin-new/ImageCropper/AdvancedImageCropperDemo';
const PaymentModal = React.lazy(() => import('./pages/PaymentManagement/PaymentModal'));
const PaymentTestSeriesModal = React.lazy(() => import('./pages/PaymentManagement/PaymentTestSeriesModal'));
const PaymentBatchModal = React.lazy(() => import('./pages/PaymentManagement/PaymentBatchModal'));
const EmbedForm = React.lazy(() => import('./VMS/EmbedForm'));

// Feature Documentation System
const FeaturesIndex = React.lazy(() => import('./pages/featuredoc').then(m => ({ default: m.FeaturesIndex })));
const FeaturePage = React.lazy(() => import('./pages/featuredoc').then(m => ({ default: m.FeaturePage })));
// UNUSED: const SessionGenerator = React.lazy(() => import('../client/pages/superadmin-new/components/SessionManagement/SessionGenerator/SessionGenerator'));
const MindMapIndex = React.lazy(() => import('./pages/MindMap'));
const FlashcardsPage = React.lazy(() => import('./pages/flashcards/FlashcardsPage'));
const TextToSpeechPage = React.lazy(() => import('./pages/TextToSpeech').then(m => ({ default: m.TextToSpeechPage })));
const ExamPage = React.lazy(() => import('./pages/exam/ExamPage'));
const ExamDetailPage = React.lazy(() => import('./pages/exam/ExamDetailPage'));
const BatchInvitationHandler = React.lazy(() => import('./pages/superadmin-new/components/ClassManagement/BatchBuilder/UserInvitationLink').then(m => ({ default: m.BatchInvitationHandler })));
const SampleCode = React.lazy(() => import('./pages/superadmin-new/ImageCroper/SampleCode'));
const ConnectAI = React.lazy(() => import('./pages/ConnectAI/ConnectAI'));
const IssueCertificateViewer = React.lazy(() => import('./pages/superadmin-new/components/CertificateManagement/IssueCertificate/IssueCertificateViewer'));
// UNUSED: const CoursePortal = React.lazy(() => import('./pages/CoursePortal/CoursePortal'));
const FormPreviewFromTab = React.lazy(() => import('./pages/superadmin-new/components/FeedbackManagement/QuestionForms/FormPreviewFromurl'));
const FeedbackInvitationHandler = React.lazy(() => import('./pages/superadmin-new/components/FeedbackManagement/QuestionForms/ActionComponents/GetLink/FeedbackInvitationHandler'));
const ExamPaperAnalyzerRoute = React.lazy(() => import('./pages/superadmin-new/components/ExamManagement/AIExamPaperAnalyser/ExamPaperAnalyzerRoute'));

// Assessment System
const AssessmentMain = React.lazy(() => import('./pages/assessment').then(m => ({ default: m.AssessmentMain })));
const AssessmentInvitationHandler = React.lazy(() => import('./pages/superadmin-new/components/AssessmentManagement/Assessment/ActionComponents/GetLink/FeedbackInvitationHandler'));
const AssessmentMain2 = React.lazy(() => import('./pages/assessment').then(m => ({ default: m.AssessmentMain2 })));
const AssessmentInvitationHandler2 = React.lazy(() => import('./pages/superadmin-new/components/AssessmentManagement/Assessment/ActionComponents/GetLink/FeedbackInvitationHandler'));

// Timetable System
const TimetablePage = React.lazy(() => import('./pages/superadmin-new/Timetable'));

// Alumni Connect System
const AlumniConnectPage = React.lazy(() => import('./pages/AlumniConnect'));

// QPaper OBE - Question Paper Builder
const QPaperOBEPage = React.lazy(() => import('./pages/qpaperOBE/QPaperOBEPage'));
const QPaperInvitationHandler = React.lazy(() => import('./pages/qpaperOBE/QPaperInvitationHandler'));

const PdfEditorPage = React.lazy(() => import('./pages/superadmin-new/components/Pdfmanagement/PdfEditorPage'));
const PdfEditorPageDnd = React.lazy(() => import('./pages/superadmin-new/components/Pdfmanagement/PdfEditorPageDnd'));
const LessonVideoPlayerPage = React.lazy(() => import('./pages/superadmin-new/components/ClassManagement/LessonPlanner/components/LessonVideoPlayerPage'));
import TwoFactorAuthentication from './pages/public/TwoFactorAuthentication';
const PaymentInvitationHandler = React.lazy(() => import('./pages/superadmin-new/components/PaymentManagement/CreatePaymentLink/PaymentInvitationHandler'));
const FeePaymentInvitationHandler = React.lazy(() => import('./pages/superadmin-new/components/InstituteManagement_new/InstituteDetail/components/tabs/Fees/components/Adjustments/FeePaymentInvitationHandler'));
const BatchMeetingsPage = React.lazy(() => import('./pages/superadmin-new/components/InstituteManagement_new/InstituteDetail/components/tabs/Academics/components/BatchSection/BatchManagement/BatchMeetingsPage'));

// SCORM Player
const ScormPlayer = React.lazy(() => import('./pages/scorm-player/ScormPlayer'));
const ScormDemo = React.lazy(() => import('./pages/scorm-player/ScormDemo'));

// Build My Site: internal QA page showing every website section (sign-in required).
const SiteSectionGallery = React.lazy(() => import('./features/site/gallery/SectionGallery'));
// Build My Site: an institute's public website on the free path address.
const PublicSite = React.lazy(() => import('./features/site/public/PublicSite'));
const TenantWebsite = React.lazy(() => import('./features/site/public/TenantWebsite'));
/**
 * On an institute's own domain these addresses are ITS website's pages
 * (terms, privacy, any page slug), never ours: our legal pages name EduExpertAI.
 */
const onOwnDomain = (platform: React.ReactNode) => (isPlatformHost() ? platform : <TenantWebsite />);
// Build My Site full screen in its own tab (Setup -> Build My Site -> Open in new tab).
const SiteBuilderPage = React.lazy(() => import('./pages/site-builder/SiteBuilderPage'));
// The saved draft, shown like the real site, in its own tab (builder "Preview").
const SitePreviewPage = React.lazy(() => import('./pages/site-builder/SitePreviewPage'));

// SCORM Course Builder
const ScormCourseBuilder = React.lazy(() => import('./pages/ScormCourse'));

// HR Onboarding
const EmployeeOnboardingHandler = React.lazy(() => import('./pages/superadmin-new/components/EmployeeView/EmployeeOnboardingHandler'));

const EvaluatorWorkspace = React.lazy(() => import('./pages/superadmin-new/components/InstituteManagement_new/InstituteDetail/components/tabs/Exams/ExamManagement/EvaluatorDesk/EvaluatorWorkspace'));

// Check if we're in development mode

const queryClient = new QueryClient();

// Auto-setup development authentication
// if (process.env.NODE_ENV === 'development') {
//   autoSetupDevelopmentAuth();
// }

const App = () => {
  useEffect(() => {
    // Mark CSS as loaded to prevent FOUC
    const root = document.getElementById('root');
    const body = document.body;

    if (root) {
      root.classList.remove('css-loading');
      root.classList.add('css-loaded');
    }

    if (body) {
      body.classList.add('css-loaded');
    }

    // Preload additional fonts after initial load
    const loadAdditionalFonts = () => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&family=Inter:wght@300;400;500;600;700;800;900&display=swap';
      link.media = 'print';
      link.onload = () => {
        link.media = 'all';
      };
      document.head.appendChild(link);
    };

    // Load additional fonts after 150ms to avoid blocking
    const timer = setTimeout(loadAdditionalFonts, 150);
    return () => clearTimeout(timer);
  }, []);

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        {/* Outside ThemeProvider: the tenant lookup must start on first mount,
            before anything renders a logo or a heading. */}
        <BrandingProvider>
        <ThemeProvider>
          <GlobalUploadProvider>
            <GlobalDownloadProvider>
            <AuthProvider>
              <Toaster />
              <Sonner />
              <AlertModal />
              <GlobalUploadManager />
              <GlobalDownloadManager />
              {/* <TokenIssueNotification /> */}
              <BrowserRouter>
                {/* App-Wide SEO Manager - Updates meta tags based on current route */}
                <RouteBasedSEO />
                {/* Global floating AI Mentor — hidden on exam, auth and public routes. */}
                <Suspense fallback={null}>
                  <AITeacherLauncher />
                  {/* "AI Mentor limit" dialog, opened from People / Batch row menus. */}
                  <AiLimitHost />
                </Suspense>

                {/* Suspense boundary for lazy-loaded routes - enables code splitting */}
                <Suspense fallback={<PageLoadingFallback />}>
                  {/* On an institute's own domain: no platform pages, and only
                      the public surface for visitors. No-op on our domains. */}
                  <TenantRouteGuard fallback={<PageLoadingFallback />}>
                  <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/login" element={<Login defaultRedirectTo="/admin_dashboard_page" />} />
                    <Route path="/2fa" element={<TwoFactorAuthentication />} />
                    <Route path="/signup" element={<SimpleSignUp />} />
                    <Route path="/signout" element={<SignOut />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/terms" element={onOwnDomain(<Terms />)} />
                    <Route path="/privacy" element={onOwnDomain(<Privacy />)} />
                    <Route path="/ourteam" element={<OurTeam />} />
                    <Route path="/cli-auth" element={<CliAuth />} />
                    <Route path="/interactive/join" element={<Join />} />
                    <Route path="/interactive/join/:sessionCode" element={<Join />} />
                    <Route path="/flashcards-demo" element={<FlashcardsPage />} />
                    <Route path="/text-to-speech" element={<TextToSpeechPage />} />
                    {/* The marketplace (features/marketplace); the old mock /courses page now opens it. */}
                    <Route path="/marketplace" element={<MarketHomePage />} />
                    <Route path="/marketplace/browse" element={<MarketBrowsePage />} />
                    <Route path="/marketplace/:kind/:id/:slug?" element={<MarketItemPage />} />
                    {/* On an institute's domain /courses is its website's Courses list. */}
                    <Route path="/courses" element={onOwnDomain(<Navigate to="/marketplace" replace />)} />
                    <Route path="/course" element={<SimplifiedCoursePage />} />
                    <Route path="/course/:courseName/:courseId" element={<SimplifiedCoursePage />} />
                    <Route path="/course/:courseName/:courseId/:userEmail" element={<SimplifiedCoursePage />} />

                    {/* Feature Documentation Routes */}
                    <Route path="/features" element={<FeaturesIndex />} />
                    <Route path="/features/:featureId" element={<FeaturePage />} />

                    <Route path="/testportal" element={<TestPortal />} />
                    <Route path="/testportal/category/:categoryId" element={<CategoryPage />} />

                    {/* Assessment System Routes */}
                    <Route path="/assessment/:assessment_id" element={<AssessmentMain />} />
                    <Route path="/assessment/user/:token" element={<AssessmentInvitationHandler />} />
                    <Route path="/assessments/:id" element={<AssessmentMain2 />} />
                    <Route path="/assessments/user/:token" element={<AssessmentInvitationHandler2 />} />

                    {/* Public certificate viewer - intended to be opened from scanned QR links.
          Example: /certificate/:qr  or /certificate?qr=<code> */}
                    <Route path="/certificate/:qr" element={<IssueCertificateViewer />} />
                    <Route path="/certificate" element={<IssueCertificateViewer />} />


                    <Route path="/forms/user/:id" element={<FormPreviewFromTab />} />
                    <Route path="/forms/users/:token" element={<FeedbackInvitationHandler />
                    } />


                    {/* Exam Routes */}
                    <Route path="/exam" element={<ExamPage />} />
                    <Route path="/exam/:examSlug" element={<ExamDetailPage />} />
                    <Route path="/evaluator-paper/:groupStudentId/:collectionId" element={<EvaluatorWorkspace />} />

                    {/* Referral Landing Page */}
                    <Route path="/r/:token" element={<ReferralLandingPage />} />
                    {/* Asset label (QR) → public asset page */}
                    <Route path="/a/:token" element={<AssetScanPage />} />

                    <Route path="/affil/campain_id/campain_Name/uuid/session_id" element={<ReferralLandingPage />} />
                    <Route path="/ref/service_id/service_Name/campain_id/campain_Name/uuid/session_id" element={<ReferralLandingPage />} />
                    <Route path="/affil/service_id/service_Name/campain_id/campain_Name/uuid/session_id" element={<ReferralLandingPage />} />

                    {/* Test Platform Routes */}
                    <Route path="/test-portal" element={<TestPortalIndex />} />
                    <Route path="/test-hub" element={<TestPortalIndex />} />
                    {/* /tests belongs to TestsLandingPage (see the Online Tests
                        User Portal block below). This route claimed the same
                        path and, being declared first, won — leaving the
                        landing page and its advertisement rail unreachable.
                        TestSelection keeps its own path. */}
                    <Route path="/test-selection" element={<TestSelection />} />
                    <Route path="/test/instructions/:id?" element={<TestInstructions />} />

                    <Route path="/test/take" element={<TestInterface />} />
                    <Route path="/test/take/:testId" element={<TestInterface />} />
                    <Route path="/test/take/demo" element={<TestInterface />} />
                    <Route path="/test/take/demo/:testId" element={<TestInterface />} />
                    <Route path="/test-demo" element={<TestDemo />} />
                    <Route path="/test/results" element={<TestResults />} />
                    <Route path="/test/written-result/:sheetId" element={<WrittenResult />} />

                    {/* Batch Invitation Routes */}
                    <Route path="/batch-request/:token" element={
                      <BatchInvitationHandler />
                    } />
                    {/* Any two-part address matches this pattern, so on an institute's
                        own domain it must stay the website's (/learn/x, /courses/x,
                        /<page>/x): only the platform host has batch-meeting links. */}
                    <Route path="/:instituteSlug/:batchToken" element={onOwnDomain(
                      <BatchMeetingsPage />
                    )} />

                    <Route path="/payment-request/:token" element={
                      <PaymentInvitationHandler />
                    } />
                    <Route path="/fee-payment-request/:token" element={
                      <FeePaymentInvitationHandler />
                    } />
                    <Route path="/payment" element={<PaymentModal />} />
                    <Route path="/coursepayment" element={<PaymentCourseModal />} />
                    <Route path="/testseriespayment" element={<PaymentTestSeriesModal />} />
                    <Route path="/batchpayment" element={<PaymentBatchModal />} />

                    <Route path="/Coursepayment" element={<PaymentModal />} />
                    <Route path="/embaded_form" element={<EmbedForm />} />

                    {/* Question Paper Generator */}
                    <Route path="/question-paper-generator" element={<QuestionPaperGenerator />} />


                    {/* Timetable Management */}
                    <Route path="/timetable" element={<TimetablePage />} />

                    {/* Alumni Connect System */}
                    <Route path="/alumniconnect" element={<AlumniConnectPage />} />

                    {/* ConnectAI Route */}
                    <Route path="/connectAI" element={<ConnectAI />} />

                    <Route path="/pdf-editor" element={<PdfEditorPageDnd />} />
                    <Route path="/pdf-editor/:id?" element={<PdfEditorPageDnd />} />
                    <Route path="/pdf-editor-classic" element={<PdfEditorPage />} />
                    <Route path="/pdf-editor-classic/:id?" element={<PdfEditorPage />} />



                    {/* <Route path="/videos" element={<VideoSection />} /> */}
                    <Route path="/videos/:id" element={<VideoPlayerPage />} />
                    <Route path="/watch/:courseId/:chapterId/:topicId/:videoId" element={<VideoWatchLinkPage />} />
                    <Route path="/lesson-video/view" element={<LessonVideoPlayerPage />} />
                    <Route path="/self-evaluation-results" element={<Evaluate />} />
                    <Route path="self-evaluation-test" element={<AssessmentMainAI />} />



                    {/* Session Management */}
                    <Route path="/site-gallery" element={<ProtectedRoute><SiteSectionGallery /></ProtectedRoute>} />
                    {/* Signed-in check is inside the page (login token, like the dashboard). */}
                    <Route path="/site-builder/:instituteId" element={<SiteBuilderPage />} />
                    <Route path="/site-preview/:instituteId/*" element={<SitePreviewPage />} />
                    {/* Institute websites (path address). The branded login comes first so
                        "login" is never read as a page slug; page slugs reserve it anyway. */}
                    <Route path="/institutes/:slug/login" element={<Login defaultRedirectTo="/admin_dashboard_page" />} />
                    {/* The rest of the sign-in flow on the institute's address, in its brand
                        (features/site/accountLinks keeps every link here). */}
                    <Route path="/institutes/:slug/forgot-password" element={<ForgotPassword />} />
                    <Route path="/institutes/:slug/2fa" element={<TwoFactorAuthentication />} />
                    <Route path="/institutes/:slug/*" element={<PublicSite />} />
                    <Route path="/sessions" element={<ProtectedRoute><SessionManagement /></ProtectedRoute>} />
                    <Route path="/sessions/my" element={<ProtectedRoute><MySessions /></ProtectedRoute>} />
                    <Route path="/create-session" element={<ProtectedRoute requiredPermission="create_session"><CreateSessionFinal /></ProtectedRoute>} />
                    <Route path="/session/:sessionId" element={<ProtectedRoute><LiveSessionControl /></ProtectedRoute>} />
                    {/* OLD ROUTES - DISABLED (folders moved to .bak) */}
                    {/* <Route path="/student/:sessionId" element={<ProtectedRoute><StudentSession /></ProtectedRoute>} /> */}

                    {/* Analytics - OLD IMPLEMENTATION DISABLED */}
                    {/* <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} /> */}
                    {/* <Route path="/analytics/:sessionId" element={<ProtectedRoute><Analytics /></ProtectedRoute>} /> */}
                    {/* New SuperAdmin Dashboard Route */}
                    {/* An institute's page: /admin_dashboard_page/institutes-new/<name-slug>/<encoded id>
                        (see utils/instituteRoute). The slug is decoration; `instituteId` is what is read. */}
                    <Route path="/admin_dashboard_page/:componentId/:instituteSlug/:instituteId" element={<SuperAdminDashboardNew />} />
                    <Route path="/admin_dashboard_page/:componentId?/:instituteId?" element={<SuperAdminDashboardNew />} />
                    <Route path="/fmea/model/:partId?" element={<ProductModellerPage />} />

                    {/* User Management Routes - OLD IMPLEMENTATION DISABLED */}
                    {/* <Route path="/user-management" element={<UserManagement />} /> */}
                    {/* COMMENTED OUT: <Route path="/user-form-demo" element={<UserFormDemo />} /> */}
                    {/* <Route path="/user-management/*" element={<UserManagement />} /> */}

                    {/* Content Sharing Management Routes */}

                    {/* Course Management Routes */}
                    <Route path="/course-management" element={<CourseManagement />} />
                    <Route path="/course-management/*" element={<CourseManagement />} />
                    <Route path="/course-content/:courseId" element={<CourseContentView />} />
                    {/* COMMENTED OUT: <Route path="/admin/course-content/:courseId" element={<AdminCourseContentView />} /> */}
                    <Route
                      path="/interactive-generator/:courseId/:topicId"
                      element={<InteractiveContentGenerator />}
                    />
                    {/* OLD ROUTE DISABLED */}
                    {/* <Route path="/student-preview" element={<StudentPreviewPage />} /> */}
                    {/* COMMENTED OUT: <Route path="/comprehensive-demo-test" element={<ComprehensiveDemoTest />} /> */}
                    {/* ?instituteId=<base64>&instituteName=… scopes it to an institute, and survives a refresh. */}
                    <Route path='/ExamPaperAnalyzer' element={<ExamPaperAnalyzerRoute />} />

                    {/* Interactive Session Routes */}
                    <Route path="/interactive" element={<InteractiveSessionList />} />
                    <Route path="/interactive/waiting" element={<WaitingPage />} />
                    <Route path="/interactive/invalid" element={<InvalidPage />} />
                    {/* <Route path="/interactive/session/:sessionId" element={<InteractiveSession />} /> */}
                    <Route path="/user/interactive/:sessionId" element={<StudentInteractiveSession />} />
                    <Route path="/interactive/instructor/:sessionId" element={<InteractiveInstructor />} />
                    <Route path="/interactive/leaderboard/:sessionId" element={<LeaderboardDashboard />} />

                    {/* Standalone tabs: the institute they file questions under has to
                        be named in the URL, or the page shows an invalid-link screen. */}
                    <Route path="/aimentor" element={<AITeacherPage />} />
                    <Route path="/aiteacher" element={<Navigate to="/aimentor" replace />} />
                    <Route path="/ai-sync" element={<AISyncPage />} />
                    <Route path="/ai-insights" element={<AIInsightsPage />} />
                    <Route path="/qgenerator" element={<QGenerator defaultSidebarOpen={true} requireInstituteScope />} />
                    <Route path="/sgenerator" element={<QGenerator requireInstituteScope />} />
                    <Route path="/qgenerator-fullpage" element={<QGeneratorFullpage />} />
                    <Route path="/qpaperOBE" element={<QPaperOBEPage />} />
                    <Route path="/qpaperOBE/:id" element={<QPaperOBEPage />} />
                    <Route path="/qpaperOBE/invite/:token" element={<QPaperInvitationHandler />} />



                    {/* Certificate Management Routes */}
                    <Route path="/certificate-management" element={<ImprovedCertificateBuilder />} />
                    <Route path="/certificate-management/test" element={<TestCertificate />} />
                    <Route path="/certificate-management/builder" element={<ImprovedCertificateBuilder />} />
                    <Route path="/certificate-management/templates" element={<EnhancedCertificateTemplates />} />
                    <Route path="/certificate-management/issuance" element={<BulkCertificateIssuance />} />
                    <Route path="/certificate-management/analytics" element={<CertificateAnalytics />} />


                    {/* Current Affairs Routes */}
                    <Route path="/currentAffairsGeneration" element={<CurrentAffairsPage />} />
                    <Route path="/currentAffairsGeneration/:id?" element={<CurrentAffairsPage />} />
                    <Route path="/currentAffairsUser" element={<CurrentAffairsUserPage />} />
                    <Route path="/currentAffairsUser/:date" element={<CurrentAffairsUserPage />} />

                    {/* Competitive Exams User Portal Routes */}
                    {/* /exams and /tests are website list pages on an institute's domain. */}
                    <Route path="/exams" element={onOwnDomain(<ExamsLandingPage />)} />
                    <Route path="/exam/:id/:slug" element={<CompetitiveExamDetailPage />} />

                    {/* Online Tests User Portal Routes */}
                    <Route path="/tests" element={onOwnDomain(<TestsLandingPage />)} />


                    {/* Notifications */}
                    <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />

                    {/* Help & Support — the requester surface. Students,
                        parents and teachers all land here; the desk lives in
                        the admin console under Support & Tickets. */}
                    <Route path="/help" element={<ProtectedRoute><HelpCenter /></ProtectedRoute>} />
                    <Route path="/help/tickets/:ticketId" element={<ProtectedRoute><HelpCenterRoute /></ProtectedRoute>} />

                    {/* User Manual */}
                    <Route path="/user-manual" element={<UserManual />} />
                    <Route path="/manual" element={<UserManual />} />
                    {/* No `/help` alias here. That path is already claimed by
                        Help & Support above, and a duplicate in the same
                        <Routes> never matched — React Router breaks the tie by
                        document order, so this only ever shadowed itself. It is
                        removed rather than left as a trap: /help is now the URL
                        in the footer of every email the product sends, and a
                        reorder that let this one win would land the whole
                        product's support traffic on the user manual. */}

                    {/* Settings and Profile */}
                    <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                    <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />

                    {/* SCORM Player */}
                    <Route path="/scorm-player/:packageId/:scoId" element={<ScormPlayer />} />
                    <Route path="/scorm-player/:packageId" element={<ScormPlayer />} />
                    {/* free ~2-minute preview, no sign-in */}
                    <Route path="/scorm-demo/:packageId" element={<ScormDemo />} />

                    {/* SCORM Course Builder (authoring — token-gated inside the page so
                        it works in a fresh/separate tab where the user object isn't rehydrated) */}
                    <Route path="/scormCourseBuilder" element={<ScormCourseBuilder />} />
                    <Route path="/scormCourseBuilder/:courseId" element={<ScormCourseBuilder />} />

                    {/* HR Management - Employee Onboarding */}
                    <Route path="/employee-onboarding" element={<EmployeeOnboardingHandler />} />
                    <Route path="/employeeOnbard" element={<EmployeeOnboardingHandler />} />

                    {/* Mind Map Routes */}
                    <Route path="/mindmap/*" element={<MindMapIndex />} />
                    {/* Catch-all route */}
                    <Route path="*" element={onOwnDomain(<NotFound />)} />
                  </Routes>
                  </TenantRouteGuard>
                </Suspense>
              </BrowserRouter>
            </AuthProvider>
            </GlobalDownloadProvider>
          </GlobalUploadProvider>
        </ThemeProvider>
        </BrandingProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
};

// Suppress ResizeObserver loop errors (common with data tables and dynamic layouts)
const originalError = console.error;
console.error = (...args) => {
  if (
    typeof args[0] === 'string' &&
    args[0].includes('ResizeObserver loop completed with undelivered notifications')
  ) {
    return; // Suppress this specific error
  }
  originalError.apply(console, args);
};

// Store the root instance to avoid multiple createRoot calls during HMR
const rootElement = document.getElementById('root')!;
let root = (globalThis as any).__reactRoot;

if (!root) {
  root = createRoot(rootElement);
  (globalThis as any).__reactRoot = root;
}

root.render(<App />);
